import { apiSlice, createClientV2QueryFn } from 'store/slices/api';
import { parseQueryParams } from 'store/slices/utils';
import {
  ClientV2PATCHResponse,
  ClientV2PATCHRequest,
  ListResponse,
  QueryParams,
} from 'types/api';
import {
  NoPartPurchaseLineSchema,
  PurchaseOrderSchemaClientV2,
  PurchaseOrderLinePATCHSchema,
  PurchaseOrderLineSchemaClientV2,
  CofactrPurchaseCharge,
} from 'types/procurement';
import { CLIENT_V2_URLS } from 'store/slices/constants/clientV2';

export const extendedApiSliceServerSide = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPurchaseOrdersClientV2: builder.query<
      ListResponse<PurchaseOrderSchemaClientV2>,
      QueryParams<PurchaseOrderSchemaClientV2>
    >({
      queryFn: createClientV2QueryFn<
        ListResponse<PurchaseOrderSchemaClientV2>,
        QueryParams<PurchaseOrderSchemaClientV2>
      >((params) =>
        CLIENT_V2_URLS.PURCHASE_ORDERS(
          parseQueryParams<PurchaseOrderSchemaClientV2>(params)
        )
      ),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: PurchaseOrderSchemaClientV2) =>
                  ({ type: 'ClientV2PurchaseOrders', id } as const)
              ),
              { type: 'ClientV2PurchaseOrders', id: 'LIST' },
            ]
          : [{ type: 'ClientV2PurchaseOrders', id: 'LIST' }],
    }),
    getPurchaseOrderLinesClientV2: builder.query<
      ListResponse<PurchaseOrderLineSchemaClientV2>,
      QueryParams<PurchaseOrderLineSchemaClientV2>
    >({
      queryFn: createClientV2QueryFn<
        ListResponse<PurchaseOrderLineSchemaClientV2>,
        QueryParams<PurchaseOrderLineSchemaClientV2>
      >((params) =>
        CLIENT_V2_URLS.PURCHASE_ORDER_LINES(
          parseQueryParams<PurchaseOrderLineSchemaClientV2>(params)
        )
      ),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: PurchaseOrderLineSchemaClientV2) =>
                  ({ type: 'ClientV2PurchaseOrderLines', id } as const)
              ),
              { type: 'ClientV2PurchaseOrderLines', id: 'LIST' },
            ]
          : [{ type: 'ClientV2PurchaseOrderLines', id: 'LIST' }],
    }),
    updatePurchaseOrderLinesClientV2: builder.mutation<
      ClientV2PATCHResponse<PurchaseOrderLineSchemaClientV2>,
      ClientV2PATCHRequest<PurchaseOrderLinePATCHSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.PURCHASE_ORDER_LINES(),
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error) => [
        { type: 'PurchaseOrders', id: 'LIST' },
        { type: 'PurchaseOrderLinesWithPurchase', id: 'LIST' },
        { type: 'PurchaseRequests', id: 'LIST' },
        ...(result
          ? result.updatedIds.map((id) => ({
              type: 'ClientV2PurchaseOrderLines' as 'ClientV2PurchaseOrderLines',
              id,
            }))
          : [
              {
                type: 'ClientV2PurchaseOrderLines' as 'ClientV2PurchaseOrderLines',
                id: 'LIST',
              },
            ]),
        ...(result
          ? result.updatedIds.map((id) => ({
              type: 'PurchaseOrderLines' as 'PurchaseOrderLines',
              id,
            }))
          : [
              {
                type: 'PurchaseOrderLines' as 'PurchaseOrderLines',
                id: 'LIST',
              },
            ]),
      ],
    }),
    getNoPartPurchaseOrderLinesClientV2: builder.query<
      ListResponse<NoPartPurchaseLineSchema>,
      QueryParams<NoPartPurchaseLineSchema>
    >({
      queryFn: createClientV2QueryFn<
        ListResponse<NoPartPurchaseLineSchema>,
        QueryParams<NoPartPurchaseLineSchema>
      >((params) =>
        CLIENT_V2_URLS.PURCHASE_ORDER_NO_PART_LINES(
          parseQueryParams<NoPartPurchaseLineSchema>(params)
        )
      ),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: NoPartPurchaseLineSchema) =>
                  ({ type: 'ClientV2PurchaseOrderNoPartLines', id } as const)
              ),
              { type: 'ClientV2PurchaseOrderNoPartLines', id: 'LIST' },
            ]
          : [{ type: 'ClientV2PurchaseOrderNoPartLines', id: 'LIST' }],
    }),
    getCofactrPurchaseChargesClientV2: builder.query<
      ListResponse<CofactrPurchaseCharge>,
      QueryParams<CofactrPurchaseCharge>
    >({
      queryFn: createClientV2QueryFn<
        ListResponse<CofactrPurchaseCharge>,
        QueryParams<CofactrPurchaseCharge>
      >((params) =>
        CLIENT_V2_URLS.COFACTR_PURCHASE_CHARGES(
          parseQueryParams<CofactrPurchaseCharge>(params)
        )
      ),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: CofactrPurchaseCharge) =>
                  ({ type: 'ClientV2CofactrPurchaseCharges', id } as const)
              ),
              { type: 'ClientV2CofactrPurchaseCharges', id: 'LIST' },
            ]
          : [{ type: 'ClientV2CofactrPurchaseCharges', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetPurchaseOrdersClientV2Query,
  useGetPurchaseOrderLinesClientV2Query,
  useGetNoPartPurchaseOrderLinesClientV2Query,
  useGetCofactrPurchaseChargesClientV2Query,
  useUpdatePurchaseOrderLinesClientV2Mutation,
  usePrefetch,
} = extendedApiSliceServerSide;
